import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LanguageTranslations } from '../../types/Language';
import {
  fetchLanguageTranslationsByCode,
  fetchLanguageTranslationsById,
  replaceLanguageTranslations,
} from '../../api/languageTranslations';
import { useLanguages } from '../../hooks/useLanguages';
import {
  flattenTranslations,
  getTranslationAtPath,
  groupTranslationLeavesBySection,
  isValidTranslationPath,
  removeTranslationAtPath,
  setTranslationAtPath,
  translationPathExists,
} from '../../utils/languageTranslations';

const REFERENCE_LANGUAGE_CODE = 'de';

const LanguageTranslationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { languages, isLoading: languagesLoading } = useLanguages({ includeInactive: true });
  const language = languages.find((entry) => entry.id === id);
  const [translations, setTranslations] = useState<LanguageTranslations>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [formError, setFormError] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newKeyPath, setNewKeyPath] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [addKeyError, setAddKeyError] = useState('');
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const {
    data: loadedTranslations,
    isLoading: translationsLoading,
    error: translationsError,
  } = useQuery({
    queryKey: ['language-translations', id],
    queryFn: () => fetchLanguageTranslationsById(id!),
    enabled: !!id,
  });

  const showReference = language?.code !== REFERENCE_LANGUAGE_CODE;

  const { data: referenceTranslations } = useQuery({
    queryKey: ['language-translations', 'reference', REFERENCE_LANGUAGE_CODE],
    queryFn: () => fetchLanguageTranslationsByCode(REFERENCE_LANGUAGE_CODE),
    enabled: showReference,
  });

  useEffect(() => {
    if (loadedTranslations) {
      setTranslations(loadedTranslations);
    }
  }, [loadedTranslations]);

  const leaves = useMemo(() => flattenTranslations(translations), [translations]);

  const filteredLeaves = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return leaves;
    }

    return leaves.filter((leaf) => {
      const referenceValue = referenceTranslations
        ? getTranslationAtPath(referenceTranslations, leaf.path) ?? ''
        : '';

      return (
        leaf.path.toLowerCase().includes(normalizedQuery) ||
        leaf.value.toLowerCase().includes(normalizedQuery) ||
        referenceValue.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [leaves, referenceTranslations, searchQuery]);

  const groupedLeaves = useMemo(
    () => groupTranslationLeavesBySection(filteredLeaves),
    [filteredLeaves]
  );

  const sectionNames = useMemo(
    () => Object.keys(groupedLeaves).sort((a, b) => a.localeCompare(b)),
    [groupedLeaves]
  );

  useEffect(() => {
    if (searchQuery.trim()) {
      setExpandedSections(sectionNames);
    }
  }, [searchQuery, sectionNames]);

  const saveMutation = useMutation({
    mutationFn: () => replaceLanguageTranslations(id!, translations),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['language-translations', id] });
      queryClient.invalidateQueries({ queryKey: ['languages'] });
      setFormError('');
      setNotification({
        open: true,
        message: t('languages.translationsSaved'),
        severity: 'success',
      });
    },
    onError: (err: unknown) => {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setFormError(axiosError.response?.data?.error || t('languages.translationsSaveFailed'));
    },
  });

  const handleCopyFromGerman = () => {
    if (!referenceTranslations) {
      return;
    }

    setTranslations(structuredClone(referenceTranslations));
    setFormError('');
  };

  const handleSectionToggle = (section: string) => {
    setExpandedSections((current) =>
      current.includes(section)
        ? current.filter((entry) => entry !== section)
        : [...current, section]
    );
  };

  const openAddKeyDialog = () => {
    setNewKeyPath('');
    setNewKeyValue('');
    setAddKeyError('');
    setAddDialogOpen(true);
  };

  const handleAddKey = () => {
    const path = newKeyPath.trim();

    if (!path) {
      setAddKeyError(t('languages.translationKeyRequired'));
      return;
    }

    if (!isValidTranslationPath(path)) {
      setAddKeyError(t('languages.translationKeyInvalid'));
      return;
    }

    if (translationPathExists(translations, path)) {
      setAddKeyError(t('languages.translationKeyExists'));
      return;
    }

    setTranslations((current) => setTranslationAtPath(current, path, newKeyValue));
    setExpandedSections((current) => {
      const section = path.split('.')[0];
      return current.includes(section) ? current : [...current, section];
    });
    setAddDialogOpen(false);
    setFormError('');
  };

  const handleDeleteKey = () => {
    if (!keyToDelete) {
      return;
    }

    setTranslations((current) => removeTranslationAtPath(current, keyToDelete));
    setKeyToDelete(null);
    setFormError('');
  };

  if (languagesLoading || translationsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!language) {
    return <Alert severity="error">{t('languages.languageNotFound')}</Alert>;
  }

  if (translationsError) {
    return <Alert severity="error">{t('languages.translationsErrorLoading')}</Alert>;
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/languages" underline="hover" color="inherit">
          {t('languages.title')}
        </Link>
        <Typography color="text.primary">
          {t('languages.editTranslationsFor', { label: language.label })}
        </Typography>
      </Breadcrumbs>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5">{t('languages.appTranslations')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('languages.appTranslationsDescription', {
              code: language.code,
              label: language.label,
            })}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/languages')}
          >
            {t('common.back')}
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openAddKeyDialog}>
            {t('languages.addTranslationKey')}
          </Button>
          {showReference && (
            <Button
              variant="outlined"
              onClick={handleCopyFromGerman}
              disabled={!referenceTranslations}
            >
              {t('languages.copyFromGerman')}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? t('common.loading') : t('common.save')}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <TextField
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={t('languages.searchTranslations')}
          fullWidth
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {filteredLeaves.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {leaves.length === 0
                ? t('languages.noTranslationsYet')
                : t('languages.noTranslationsFound')}
            </Typography>
            {leaves.length === 0 && (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={openAddKeyDialog}>
                {t('languages.addTranslationKey')}
              </Button>
            )}
          </Box>
        ) : (
          sectionNames.map((section) => (
            <Accordion
              key={section}
              expanded={expandedSections.includes(section)}
              onChange={() => handleSectionToggle(section)}
              disableGutters
              sx={{ '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>{section}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({groupedLeaves[section].length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {groupedLeaves[section].map((leaf) => {
                  const referenceValue = referenceTranslations
                    ? getTranslationAtPath(referenceTranslations, leaf.path)
                    : undefined;

                  return (
                    <Box key={leaf.path} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        label={leaf.path}
                        value={leaf.value}
                        onChange={(event) =>
                          setTranslations((current) =>
                            setTranslationAtPath(current, leaf.path, event.target.value)
                          )
                        }
                        fullWidth
                        multiline={leaf.value.length > 80}
                        minRows={leaf.value.length > 80 ? 2 : 1}
                        helperText={
                          showReference && referenceValue
                            ? t('languages.germanReference', { value: referenceValue })
                            : undefined
                        }
                      />
                      <IconButton
                        color="error"
                        onClick={() => setKeyToDelete(leaf.path)}
                        aria-label={t('languages.deleteTranslationKey')}
                        sx={{ mt: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Paper>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('languages.addTranslationKey')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {addKeyError && <Alert severity="error">{addKeyError}</Alert>}
            <TextField
              label={t('languages.translationKey')}
              value={newKeyPath}
              onChange={(event) => setNewKeyPath(event.target.value)}
              fullWidth
              required
              helperText={t('languages.translationKeyHelp')}
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
            <TextField
              label={t('languages.translationValue')}
              value={newKeyValue}
              onChange={(event) => setNewKeyValue(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleAddKey}>
            {t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!keyToDelete} onClose={() => setKeyToDelete(null)}>
        <DialogTitle>{t('languages.confirmDeleteTranslationKey')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('languages.deleteTranslationKeyConfirmation', { key: keyToDelete })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeyToDelete(null)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDeleteKey}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LanguageTranslationsPage;
